/* global AbortController, customElements, document, HTMLDetailsElement, HTMLElement, Node, window */

if (!customElements.get('lp-header')) {
  customElements.define(
    'lp-header',
    class LPHeader extends HTMLElement {
      connectedCallback() {
        if (this.connectionAbortController) {
          return
        }

        this.connectionAbortController = new AbortController()
        this.connectionGeneration = (this.connectionGeneration || 0) + 1
        const { signal } = this.connectionAbortController

        this.desktopQuery = window.matchMedia('(min-width: 1024px)')
        this.menuTriggers = Array.from(this.querySelectorAll('[data-lp-menu-trigger]'))
        this.menuCloseTriggers = Array.from(this.querySelectorAll('[data-lp-menu-close-trigger]'))
        this.desktopPanels = Array.from(this.querySelectorAll('.lp-header__mega'))
        this.menuBoundary = this.querySelector('[data-lp-menu-boundary]')
        this.overlay = this.querySelector('[data-lp-overlay]')
        this.drawer = this.querySelector('.lp-header__drawer')
        this.drawerOpenButton = this.querySelector('[data-lp-drawer-open]')
        this.drawerCloseButtons = Array.from(this.querySelectorAll('[data-lp-drawer-close]'))
        this.drawerSections = Array.from(this.querySelectorAll('[data-lp-drawer-section]'))
        this.drawerSecondaryTabs = Array.from(
          this.querySelectorAll('[data-lp-drawer-secondary-tab]'),
        )
        this.drawerPageMoreButtons = Array.from(this.querySelectorAll('[data-lp-page-more]'))
        this.drawerMarketButton = this.querySelector('[data-lp-drawer-market-open]')
        this.drawerMarketLabel = this.querySelector('[data-lp-drawer-market-label]')
        this.collectionTabs = Array.from(this.querySelectorAll('[data-lp-collection-tab]'))
        this.cartButton = this.querySelector('[data-lp-cart-trigger]')
        this.accountMenu = this.querySelector('[data-lp-account-menu]')
        this.accountMenuTrigger = this.querySelector('[data-lp-account-trigger]')
        this.backToTopButton = document.getElementById(
          `lp-header-back-to-top-${this.dataset.sectionId}`,
        )
        this.menuOpenTrigger = this.dataset.menuOpenTrigger || 'hover'
        this.lastDrawerTrigger = null
        this.desktopMenuCloseTimer = null
        this.headerVisibilityTimer = null
        this.marketOpenTimer = null
        this.externalSyncTimer = null
        this.pageScrollLockReasons = new Set()
        this.ownsPageScrollLock = false
        this.lastScrollTop = window.scrollY
        this.accumulatedScroll = 0
        this.isHeaderVisible = true
        this.externalProviders = new Set()
        this.accountFocusFromPointer = false
        this.suppressAccountFocusOpen = false
        this.onDesktopMenuSurfaceScroll = (event) => this.handleDesktopMenuSurfaceScroll(event)

        this.menuTriggers.forEach((trigger) => {
          trigger.addEventListener(
            'mouseenter',
            () => {
              if (this.menuOpenTrigger === 'hover') {
                this.clearDesktopMenuCloseTimer()
                this.openDesktopMenu(trigger)
              }
            },
            { signal },
          )
          trigger.addEventListener('focus', () => this.openDesktopMenu(trigger), { signal })
          trigger.addEventListener(
            'click',
            () => {
              if (this.menuOpenTrigger === 'click') {
                this.toggleDesktopMenu(trigger)
              }
            },
            { signal },
          )
        })
        this.menuCloseTriggers.forEach((trigger) => {
          trigger.addEventListener('mouseenter', () => this.closeDesktopMenuOnHover(), { signal })
        })

        this.querySelectorAll('[data-lp-category]').forEach((button) => {
          button.addEventListener('mouseenter', () => this.activateCategory(button), { signal })
          button.addEventListener('focus', () => this.activateCategory(button), { signal })
          button.addEventListener('click', () => this.activateCategory(button), { signal })
        })

        this.collectionTabs.forEach((tab) => {
          tab.addEventListener('mouseenter', () => this.activateCollectionTab(tab), { signal })
          tab.addEventListener('click', () => this.activateCollectionTab(tab), { signal })
          tab.addEventListener('keydown', (event) => this.handleCollectionTabKeydown(event), {
            signal,
          })
        })

        this.overlay?.addEventListener('click', () => this.closeDesktopMenu(), { signal })
        this.addEventListener('wheel', this.onDesktopMenuSurfaceScroll, {
          passive: false,
          signal,
        })
        this.addEventListener('touchmove', this.onDesktopMenuSurfaceScroll, {
          passive: false,
          signal,
        })
        this.cartButton?.addEventListener(
          'click',
          () => {
            this.closeDesktopMenu()
            this.closeAccountMenu()
          },
          { capture: true, signal },
        )
        this.accountMenu?.addEventListener('toggle', () => this.syncAccountMenu(), { signal })
        this.accountMenuTrigger?.addEventListener(
          'pointerdown',
          () => {
            this.accountFocusFromPointer = true
          },
          { signal },
        )
        this.accountMenu?.addEventListener(
          'pointerenter',
          (event) => this.handleAccountPointerEnter(event),
          { signal },
        )
        this.accountMenu?.addEventListener(
          'pointerleave',
          (event) => this.handleAccountPointerLeave(event),
          { signal },
        )
        this.accountMenu?.addEventListener('focusin', () => this.handleAccountFocusIn(), {
          signal,
        })
        this.accountMenu?.addEventListener(
          'focusout',
          (event) => this.handleAccountFocusOut(event),
          { signal },
        )
        this.drawerOpenButton?.addEventListener('click', () => this.openDrawer(), { signal })
        this.drawerCloseButtons.forEach((button) =>
          button.addEventListener('click', () => this.closeDrawer(), { signal }),
        )
        this.onDrawerSectionToggle = (event) => this.handleDrawerSectionToggle(event.currentTarget)
        this.drawerSections.forEach((section) =>
          section.addEventListener('toggle', this.onDrawerSectionToggle, { signal }),
        )
        this.onDrawerSecondaryTabToggle = (event) =>
          this.handleDrawerSecondaryTabToggle(event.currentTarget)
        this.drawerSecondaryTabs.forEach((secondaryTab) =>
          secondaryTab.addEventListener('toggle', this.onDrawerSecondaryTabToggle, { signal }),
        )
        this.onDrawerPageMoreClick = (event) => this.expandDrawerPageMenu(event.currentTarget)
        this.drawerPageMoreButtons.forEach((button) =>
          button.addEventListener('click', this.onDrawerPageMoreClick, { signal }),
        )
        this.onDrawerMarketClick = () => this.openMarketSelector()
        this.drawerMarketButton?.addEventListener('click', this.onDrawerMarketClick, { signal })
        this.onBackToTopClick = (event) => this.scrollToTop(event)
        this.backToTopButton?.addEventListener('click', this.onBackToTopClick, { signal })
        this.onBackToTopBlur = () => this.syncBackToTop({ preserveFocusedButton: false })
        this.backToTopButton?.addEventListener('blur', this.onBackToTopBlur, { signal })

        this.onDocumentKeydown = (event) => {
          if (event.key !== 'Escape') {
            this.handleDesktopMenuBackgroundKeydown(event)
            return
          }

          if (this.drawer && !this.drawer.hidden) {
            this.closeDrawer()
            return
          }

          if (this.hasOpenAccountMenu()) {
            this.closeAccountMenu({ restoreFocus: true })
            return
          }

          this.closeDesktopMenu()
        }

        this.onDocumentPointerDown = (event) => {
          if (
            this.hasOpenAccountMenu() &&
            event.target instanceof Node &&
            !this.accountMenu.contains(event.target)
          ) {
            this.closeAccountMenu()
          }
        }

        this.onDocumentPointerMove = (event) => {
          if (!this.desktopQuery.matches || !this.hasOpenDesktopMenu()) {
            return
          }

          if (this.isPointerInsideDesktopMenu(event.clientX, event.clientY)) {
            this.clearDesktopMenuCloseTimer()
          } else {
            this.scheduleDesktopMenuClose()
          }
        }

        this.onDocumentMouseout = (event) => {
          if (this.desktopQuery.matches && event.relatedTarget === null && event.clientY <= 0) {
            this.closeDesktopMenu()
          }
        }

        this.onDocumentPointerEnd = () => {
          this.accountFocusFromPointer = false
        }

        this.onWindowScroll = () => {
          this.handleHideOnScroll()
          this.checkTransparency()
          this.syncBackToTop()
          if (this.hasOpenDesktopMenu()) {
            this.updateOverlayStart()
          }
        }
        this.onWindowResize = () => {
          this.updateOverlayStart()
          this.syncBackToTop()
        }

        this.onDesktopQueryChange = () => {
          if (this.desktopQuery.matches) {
            const drawerWasOpen = this.hasOpenDrawer()
            this.closeDrawer({ restoreFocus: false })

            if (drawerWasOpen) {
              this.focusHeaderLogo()
            }
          } else {
            this.closeDesktopMenu()
            this.closeAccountMenu()
          }

          this.syncExternalProviders()
        }
        this.onShopifySectionLoad = () => {
          this.syncExternalProviders()
          this.syncDrawerMarketLabel()
        }
        this.onShopifySectionUnload = (event) => this.handleExternalSectionUnload(event)
        this.onShopifySectionSelect = (event) => this.handleExternalSectionSelect(event)

        document.addEventListener('keydown', this.onDocumentKeydown, { signal })
        document.addEventListener('pointerdown', this.onDocumentPointerDown, { signal })
        document.addEventListener('pointerup', this.onDocumentPointerEnd, { signal })
        document.addEventListener('pointercancel', this.onDocumentPointerEnd, { signal })
        document.addEventListener('pointermove', this.onDocumentPointerMove, { signal })
        document.addEventListener('mouseout', this.onDocumentMouseout, { signal })
        document.addEventListener('shopify:section:load', this.onShopifySectionLoad, { signal })
        document.addEventListener('shopify:section:unload', this.onShopifySectionUnload, { signal })
        document.addEventListener('shopify:section:select', this.onShopifySectionSelect, { signal })
        window.addEventListener('scroll', this.onWindowScroll, { signal })
        window.addEventListener('resize', this.onWindowResize, { signal })
        this.desktopQuery.addEventListener('change', this.onDesktopQueryChange, { signal })
        this.syncExternalProviders()
        this.syncDrawerMarketLabel()
        this.updateOverlayStart()
        this.setupTransparentHeader()
        this.syncBackToTop()
      }

      disconnectedCallback() {
        if (!this.connectionAbortController) {
          return
        }

        this.connectionGeneration += 1
        this.connectionAbortController.abort()
        this.connectionAbortController = null
        this.accountFocusFromPointer = false
        this.suppressAccountFocusOpen = false
        this.clearDesktopMenuCloseTimer()
        this.clearTimer('headerVisibilityTimer')
        this.clearTimer('marketOpenTimer')
        this.clearTimer('externalSyncTimer')
        this.restoreExternalProviders()
        this.pageScrollLockReasons.clear()
        this.releasePageScrollLock()
      }

      clearTimer(timerName) {
        if (!this[timerName]) {
          return
        }

        window.clearTimeout(this[timerName])
        this[timerName] = null
      }

      toggleDesktopMenu(trigger) {
        if (!this.desktopQuery.matches) {
          return
        }

        if (this.getDesktopPanelForTrigger(trigger)?.classList.contains('is-open')) {
          this.closeDesktopMenu()
        } else {
          this.openDesktopMenu(trigger)
        }
      }

      openDesktopMenu(trigger) {
        if (!this.desktopQuery.matches) {
          return
        }

        this.clearDesktopMenuCloseTimer()
        this.closeAccountMenu()
        const currentItem = trigger.closest('.lp-header__nav-item')
        const currentPanel = this.getDesktopPanelForTrigger(trigger)

        if (!currentItem || !currentPanel || currentPanel.hidden) {
          this.closeDesktopMenu()
          return
        }

        this.menuTriggers.forEach((itemTrigger) => {
          const item = itemTrigger.closest('.lp-header__nav-item')
          const isCurrent = item === currentItem
          item?.classList.toggle('is-open', isCurrent)
          itemTrigger.setAttribute('aria-expanded', String(isCurrent))
        })

        this.desktopPanels.forEach((panel) => {
          panel.classList.toggle('is-open', panel === currentPanel)
        })
        this.updateOverlayStart()
        if (this.overlay) {
          this.overlay.hidden = false
        }

        this.checkTransparency()
      }

      closeDesktopMenu() {
        this.clearDesktopMenuCloseTimer()
        this.menuTriggers.forEach((trigger) => {
          trigger.closest('.lp-header__nav-item')?.classList.remove('is-open')
          trigger.setAttribute('aria-expanded', 'false')
        })
        this.desktopPanels.forEach((panel) => panel.classList.remove('is-open'))

        if (this.overlay) {
          this.overlay.hidden = true
        }
        this.checkTransparency()
      }

      hasOpenAccountMenu() {
        return Boolean(this.accountMenu?.open)
      }

      hasHoverAccountMenu() {
        return Boolean(this.accountMenu?.hasAttribute('data-lp-account-hover'))
      }

      openAccountMenu() {
        if (!this.desktopQuery.matches || !this.hasHoverAccountMenu()) {
          return
        }

        this.accountMenu.open = true
        this.syncAccountMenu()
      }

      handleAccountPointerEnter(event) {
        if (event.pointerType !== 'mouse') {
          return
        }

        this.openAccountMenu()
      }

      handleAccountPointerLeave(event) {
        if (event.pointerType !== 'mouse' || !this.hasHoverAccountMenu()) {
          return
        }

        if (event.relatedTarget && this.accountMenu.contains(event.relatedTarget)) {
          return
        }

        if (document.activeElement && this.accountMenu.contains(document.activeElement)) {
          return
        }

        this.closeAccountMenu()
      }

      handleAccountFocusIn() {
        if (this.accountFocusFromPointer || this.suppressAccountFocusOpen) {
          return
        }

        this.openAccountMenu()
      }

      handleAccountFocusOut(event) {
        if (!this.hasHoverAccountMenu()) {
          return
        }

        if (event.relatedTarget && this.accountMenu.contains(event.relatedTarget)) {
          return
        }

        this.closeAccountMenu()
      }

      syncAccountMenu() {
        const isOpen = this.hasOpenAccountMenu()
        this.accountMenuTrigger?.setAttribute('aria-expanded', String(isOpen))

        if (isOpen) {
          this.closeDesktopMenu()
        }

        this.checkTransparency()
      }

      closeAccountMenu({ restoreFocus = false } = {}) {
        const wasOpen = this.hasOpenAccountMenu()

        if (this.accountMenu) {
          this.accountMenu.open = false
        }

        this.accountMenuTrigger?.setAttribute('aria-expanded', 'false')
        this.checkTransparency()

        if (wasOpen && restoreFocus) {
          this.suppressAccountFocusOpen = true

          try {
            this.accountMenuTrigger?.focus()
          } finally {
            this.suppressAccountFocusOpen = false
          }
        }
      }

      closeDesktopMenuOnHover() {
        if (this.menuOpenTrigger !== 'hover') {
          return
        }

        this.closeDesktopMenu()
      }

      scheduleDesktopMenuClose() {
        this.clearDesktopMenuCloseTimer()
        this.desktopMenuCloseTimer = window.setTimeout(() => this.closeDesktopMenu(), 180)
      }

      clearDesktopMenuCloseTimer() {
        if (!this.desktopMenuCloseTimer) {
          return
        }

        window.clearTimeout(this.desktopMenuCloseTimer)
        this.desktopMenuCloseTimer = null
      }

      hasOpenDesktopMenu() {
        return this.desktopPanels.some((panel) => panel.classList.contains('is-open'))
      }

      handleDesktopMenuSurfaceScroll(event) {
        if (
          !this.desktopQuery.matches ||
          !this.hasOpenDesktopMenu() ||
          (event.type === 'wheel' && event.ctrlKey) ||
          (event.type === 'touchmove' && event.touches.length !== 1)
        ) {
          return
        }

        const activePanel = this.getActiveDesktopPanel()
        const target = this.getDesktopMenuEventTarget(event)

        if (!activePanel?.contains(target)) {
          event.preventDefault()
        }
      }

      handleDesktopMenuBackgroundKeydown(event) {
        if (
          !this.desktopQuery.matches ||
          !this.hasOpenDesktopMenu() ||
          event.defaultPrevented ||
          event.altKey ||
          ![' ', 'Spacebar', 'ArrowDown', 'ArrowUp', 'PageDown', 'PageUp', 'Home', 'End'].includes(
            event.key,
          )
        ) {
          return
        }

        const target = this.getDesktopMenuEventTarget(event)

        if (
          target?.closest('.lp-header__mega.is-open') ||
          target?.matches('input, select, textarea, [contenteditable]') ||
          (event.ctrlKey && (event.key === 'PageUp' || event.key === 'PageDown')) ||
          ((event.key === ' ' || event.key === 'Spacebar') &&
            target?.matches('button, summary, [role="button"]'))
        ) {
          return
        }

        event.preventDefault()
      }

      getDesktopMenuEventTarget(event) {
        return event.target instanceof HTMLElement ? event.target : event.target?.parentElement
      }

      hasOpenDrawer() {
        return Boolean(this.drawer && !this.drawer.hidden)
      }

      getActiveDesktopPanel() {
        return this.desktopPanels.find((panel) => panel.classList.contains('is-open'))
      }

      getDesktopPanelForTrigger(trigger) {
        return this.desktopPanels.find(
          (panel) => panel.dataset.lpMenuPanel === trigger.dataset.lpMenuTrigger,
        )
      }

      updateOverlayStart() {
        const boundaryBottom = this.menuBoundary?.getBoundingClientRect().bottom

        if (typeof boundaryBottom !== 'number') {
          return
        }

        this.style.setProperty('--lp-header-overlay-start', `${Math.max(0, boundaryBottom)}px`)
      }

      syncDrawerMarketLabel() {
        const sourceLabel = document.querySelector(
          'lp-market-topbar [data-lp-market-current-label]',
        )
        const label = sourceLabel?.textContent?.trim()

        if (label && this.drawerMarketLabel) {
          this.drawerMarketLabel.textContent = label
        }
      }

      openMarketSelector() {
        const marketTrigger = document.querySelector('lp-market-topbar [data-lp-market-open]')

        if (!(marketTrigger instanceof HTMLElement)) {
          return
        }

        const marketOwner = marketTrigger.closest('lp-market-topbar')

        if (!(marketOwner instanceof HTMLElement) || typeof marketOwner.open !== 'function') {
          return
        }

        const generation = this.connectionGeneration
        this.closeDrawer()
        this.clearTimer('marketOpenTimer')
        this.marketOpenTimer = window.setTimeout(() => {
          this.marketOpenTimer = null

          if (this.isActiveConnection(generation)) {
            marketOwner.open(marketTrigger, this.drawerOpenButton)
          }
        }, 0)
      }

      isPointerInsideDesktopMenu(x, y) {
        const rects = [
          this.menuBoundary?.getBoundingClientRect(),
          this.getActiveDesktopPanel()?.getBoundingClientRect(),
        ].filter(Boolean)

        return rects.some((rect) => this.isPointInsideRect(x, y, rect))
      }

      isPointInsideRect(x, y, rect) {
        return x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom
      }

      syncExternalProviders() {
        document.querySelectorAll('[data-lp-external-provider]').forEach((provider) => {
          this.externalProviders.add(provider)
        })

        this.externalProviders.forEach((provider) => {
          if (!provider.isConnected) {
            this.externalProviders.delete(provider)
          }
        })

        const menuKeys = new Set(
          Array.from(this.querySelectorAll('[data-lp-external-desktop-host]'))
            .map((host) => host.dataset.lpMenuKey)
            .filter(Boolean),
        )
        const mountedProviders = new Set()

        menuKeys.forEach((menuKey) => {
          const provider = Array.from(this.externalProviders).find(
            (candidate) =>
              candidate.dataset.lpMenuKey === menuKey &&
              candidate.querySelector('[data-lp-content-item]'),
          )
          const targetHost = this.findExternalHost(
            this.desktopQuery.matches
              ? '[data-lp-external-desktop-host]'
              : '[data-lp-external-mobile-host]',
            menuKey,
          )
          const isAvailable = Boolean(provider && targetHost)

          this.updateExternalMenuAvailability(menuKey, isAvailable)

          if (!isAvailable) {
            return
          }

          targetHost.append(provider)
          provider.hidden = false
          mountedProviders.add(provider)
        })

        this.externalProviders.forEach((provider) => {
          if (!mountedProviders.has(provider)) {
            this.restoreExternalProvider(provider)
          }
        })
      }

      findExternalHost(selector, menuKey) {
        return Array.from(this.querySelectorAll(selector)).find(
          (element) => element.dataset.lpMenuKey === menuKey,
        )
      }

      updateExternalMenuAvailability(menuKey, isAvailable) {
        const keyedElements = Array.from(this.querySelectorAll('[data-lp-menu-key]')).filter(
          (element) => element.dataset.lpMenuKey === menuKey,
        )

        keyedElements.forEach((element) => {
          if (
            element.matches(
              '[data-lp-external-trigger], [data-lp-external-mobile-section], [data-lp-menu-panel]',
            )
          ) {
            element.hidden = !isAvailable
          }

          if (element.matches('[data-lp-external-fallback], [data-lp-external-mobile-fallback]')) {
            element.hidden = isAvailable
          }

          if (!isAvailable && element.matches('[data-lp-menu-panel].is-open')) {
            this.closeDesktopMenu()
          }
        })
      }

      restoreExternalProvider(provider) {
        const sectionId = provider.dataset.lpProviderSectionId
        const source = sectionId ? document.getElementById(`shopify-section-${sectionId}`) : null

        if (source && provider.parentElement !== source) {
          source.append(provider)
        }

        provider.hidden = true
      }

      restoreExternalProviders() {
        this.externalProviders?.forEach((provider) => this.restoreExternalProvider(provider))
      }

      handleExternalSectionUnload(event) {
        const sectionId =
          event.detail?.sectionId ||
          event.target?.id?.replace(/^shopify-section-/, '') ||
          event.target?.dataset?.sectionId

        if (!sectionId) {
          return
        }

        let restoredProvider = false

        this.externalProviders.forEach((provider) => {
          if (provider.dataset.lpProviderSectionId !== sectionId) {
            return
          }

          restoredProvider = true
          const menuKey = provider.dataset.lpMenuKey

          if (event.target instanceof HTMLElement) {
            event.target.append(provider)
          } else {
            this.restoreExternalProvider(provider)
          }

          provider.hidden = true
          this.externalProviders.delete(provider)

          if (menuKey) {
            this.updateExternalMenuAvailability(menuKey, false)
          }
        })

        if (restoredProvider) {
          const generation = this.connectionGeneration
          this.clearTimer('externalSyncTimer')
          this.externalSyncTimer = window.setTimeout(() => {
            this.externalSyncTimer = null

            if (this.isActiveConnection(generation)) {
              this.syncExternalProviders()
            }
          }, 0)
        }
      }

      handleExternalSectionSelect(event) {
        this.syncExternalProviders()

        const sectionId =
          event.detail?.sectionId ||
          event.target?.id?.replace(/^shopify-section-/, '') ||
          event.target?.dataset?.sectionId
        const provider = Array.from(this.externalProviders).find(
          (candidate) => candidate.dataset.lpProviderSectionId === sectionId,
        )

        if (!provider) {
          return
        }

        const menuKey = provider.dataset.lpMenuKey

        if (this.desktopQuery.matches) {
          const trigger = Array.from(this.querySelectorAll('[data-lp-external-trigger]')).find(
            (candidate) => candidate.dataset.lpMenuKey === menuKey,
          )

          if (trigger && !trigger.hidden) {
            this.openDesktopMenu(trigger)
          }

          return
        }

        this.openDrawer()
        const drawerSection = this.findExternalHost('[data-lp-external-mobile-section]', menuKey)

        if (drawerSection instanceof HTMLDetailsElement) {
          drawerSection.open = true
        }
      }

      handleHideOnScroll() {
        if (
          !this.hasAttribute('hide-on-scroll') ||
          !window.matchMedia('(prefers-reduced-motion: no-preference)').matches
        ) {
          return
        }

        if (window.scrollY < 0) {
          return
        }

        this.accumulatedScroll = Math.max(
          0,
          this.accumulatedScroll + window.scrollY - this.lastScrollTop,
        )

        if (window.scrollY < this.lastScrollTop) {
          this.showHeader()
        } else if (
          this.accumulatedScroll > Number.parseInt(this.getAttribute('hide-on-scroll'), 10) &&
          !this.hasOpenDesktopMenu() &&
          !this.hasOpenAccountMenu()
        ) {
          this.hideHeader()
        }

        this.lastScrollTop = window.scrollY
      }

      syncBackToTop({ preserveFocusedButton = true } = {}) {
        if (!this.backToTopButton) {
          return
        }

        const shouldShow = window.scrollY >= Math.max(600, window.innerHeight)
        const keepFocusedButtonVisible =
          preserveFocusedButton && document.activeElement === this.backToTopButton
        this.backToTopButton.hidden = !shouldShow && !keepFocusedButtonVisible
      }

      focusHeaderLogo() {
        this.querySelector('.lp-header__logo-link')?.focus({ preventScroll: true })
      }

      scrollToTop(event) {
        const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches
          ? 'auto'
          : 'smooth'
        this.showHeader()
        window.scrollTo({ top: 0, behavior })

        if (event.detail === 0) {
          this.focusHeaderLogo()
        } else {
          this.backToTopButton?.blur?.()
        }
      }

      hideHeader() {
        if (!this.isHeaderVisible) {
          return
        }

        const generation = this.connectionGeneration
        this.isHeaderVisible = false
        document.documentElement.style.setProperty('--header-is-visible', '0')
        this.style.transform = 'translateY(-100%)'

        this.clearTimer('headerVisibilityTimer')
        this.headerVisibilityTimer = window.setTimeout(() => {
          this.headerVisibilityTimer = null

          if (this.isActiveConnection(generation) && !this.isHeaderVisible) {
            this.closest('.shopify-section').style.visibility = 'hidden'
          }
        }, 200)
      }

      showHeader() {
        if (this.isHeaderVisible) {
          return
        }

        this.clearTimer('headerVisibilityTimer')
        this.closest('.shopify-section').style.visibility = 'visible'
        document.documentElement.style.setProperty('--header-is-visible', '1')
        this.style.transform = 'translateY(0)'
        this.accumulatedScroll = 0
        this.isHeaderVisible = true
      }

      setupTransparentHeader() {
        const transparentOwner = document.querySelector(
          '.shopify-section:first-child [allow-transparent-header]',
        )

        if (transparentOwner) {
          this.setAttribute('allow-transparency', '')
          const transparentTone = transparentOwner.dataset.transparentHeaderTone

          if (transparentOwner.hasAttribute('data-transparent-header-on-scroll')) {
            this.setAttribute('transparent-header-on-scroll', '')
          } else {
            this.removeAttribute('transparent-header-on-scroll')
          }

          if (transparentTone) {
            this.setAttribute('transparent-header-tone', transparentTone)
          } else {
            this.removeAttribute('transparent-header-tone')
          }
        } else {
          this.removeAttribute('allow-transparency')
          this.removeAttribute('transparent-header-tone')
          this.removeAttribute('transparent-header-on-scroll')
        }

        this.checkTransparency()
      }

      checkTransparency() {
        const hasFallbackScrollDetection =
          this.hasAttribute('sticky') &&
          !this.hasAttribute('transparent-header-on-scroll') &&
          window.scrollY > 20
        this.classList.toggle(
          'is-filled',
          !this.hasAttribute('allow-transparency') ||
            this.hasOpenDesktopMenu() ||
            this.hasOpenAccountMenu() ||
            this.hasOpenDrawer() ||
            hasFallbackScrollDetection,
        )
      }

      activateCategory(button) {
        const category = button.dataset.lpCategory
        const menu = button.closest('.lp-header__mega--products')

        if (!category || !menu) {
          return
        }

        menu.querySelectorAll('[data-lp-category]').forEach((categoryButton) => {
          categoryButton.classList.toggle('is-active', categoryButton === button)
        })

        menu.querySelectorAll('[data-lp-category-panel]').forEach((panel) => {
          panel.classList.toggle('hidden', panel.dataset.lpCategoryPanel !== category)
        })
      }

      activateCollectionTab(tab) {
        const menu = tab.closest('[data-lp-tabbed-menu]')
        const panelId = tab.getAttribute('aria-controls')

        if (!menu || !panelId) {
          return
        }

        menu.querySelectorAll('[data-lp-collection-tab]').forEach((candidate) => {
          const isActive = candidate === tab
          candidate.setAttribute('aria-selected', String(isActive))
          candidate.setAttribute('tabindex', isActive ? '0' : '-1')
        })

        menu.querySelectorAll('[data-lp-collection-panel]').forEach((panel) => {
          panel.hidden = panel.id !== panelId
        })
      }

      handleCollectionTabKeydown(event) {
        const tab = event.currentTarget
        const menu = tab instanceof HTMLElement ? tab.closest('[data-lp-tabbed-menu]') : null
        const tabs = menu
          ? Array.from(menu.querySelectorAll('[data-lp-collection-tab]')).filter(
              (candidate) => !candidate.hidden,
            )
          : []
        const currentIndex = tabs.indexOf(tab)

        if (currentIndex < 0) {
          return
        }

        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault()
          this.activateCollectionTab(tab)
          return
        }

        let nextIndex

        if (['ArrowLeft', 'ArrowUp'].includes(event.key)) {
          nextIndex = (currentIndex - 1 + tabs.length) % tabs.length
        } else if (['ArrowRight', 'ArrowDown'].includes(event.key)) {
          nextIndex = (currentIndex + 1) % tabs.length
        } else if (event.key === 'Home') {
          nextIndex = 0
        } else if (event.key === 'End') {
          nextIndex = tabs.length - 1
        } else {
          return
        }

        event.preventDefault()
        tabs[nextIndex]?.focus()
      }

      handleDrawerSectionToggle(section) {
        if (!section.open) {
          return
        }

        this.drawerSections.forEach((otherSection) => {
          if (otherSection !== section) {
            otherSection.open = false
          }
        })

        const summary = section.querySelector(':scope > .lp-header__drawer-summary')

        if (!(summary instanceof HTMLElement)) {
          return
        }

        const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches
          ? 'auto'
          : 'smooth'

        summary.scrollIntoView({ behavior, block: 'start' })
      }

      handleDrawerSecondaryTabToggle(secondaryTab) {
        if (!secondaryTab.open) {
          return
        }

        const section = secondaryTab.closest('[data-lp-drawer-section]')

        if (!section) {
          return
        }

        section.querySelectorAll('[data-lp-drawer-secondary-tab]').forEach((otherTab) => {
          if (otherTab !== secondaryTab) {
            otherTab.open = false
          }
        })

        const summary = secondaryTab.querySelector(':scope > summary')

        if (!(summary instanceof HTMLElement)) {
          return
        }

        const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches
          ? 'auto'
          : 'smooth'

        summary.scrollIntoView({ behavior, block: 'start' })
      }

      expandDrawerPageMenu(button) {
        const pageContent = button.closest('.lp-header__drawer-content--pages')
        const moreWrapper = button.closest('.lp-header__drawer-page-more')

        if (!pageContent || !moreWrapper) {
          return
        }

        pageContent.querySelectorAll('[data-lp-page-overflow]').forEach((overflowItem) => {
          overflowItem.hidden = false
        })
        button.setAttribute('aria-expanded', 'true')
        moreWrapper.hidden = true
      }

      resetDrawerPageMenus() {
        this.drawerPageMoreButtons.forEach((button) => {
          const pageContent = button.closest('.lp-header__drawer-content--pages')
          const moreWrapper = button.closest('.lp-header__drawer-page-more')

          pageContent?.querySelectorAll('[data-lp-page-overflow]').forEach((overflowItem) => {
            overflowItem.hidden = true
          })
          button.setAttribute('aria-expanded', 'false')

          if (moreWrapper) {
            moreWrapper.hidden = false
          }
        })
      }

      closeDrawerSections() {
        this.drawerSections.forEach((section) => {
          section.open = false
        })
      }

      openDrawer() {
        if (!this.drawer) {
          return
        }

        this.lastDrawerTrigger = document.activeElement
        this.closeAccountMenu()
        this.closeDrawerSections()
        this.resetDrawerPageMenus()
        this.drawer.hidden = false
        this.drawerOpenButton?.setAttribute('aria-expanded', 'true')
        this.lockPageScroll('drawer')
        this.checkTransparency()
        this.drawer.querySelector('[data-lp-drawer-close]')?.focus()
      }

      closeDrawer({ restoreFocus = true } = {}) {
        if (!this.drawer) {
          return
        }

        const wasOpen = !this.drawer.hidden
        this.drawer.hidden = true
        this.drawerOpenButton?.setAttribute('aria-expanded', 'false')
        this.unlockPageScroll('drawer')
        this.checkTransparency()

        if (wasOpen && restoreFocus && this.lastDrawerTrigger instanceof HTMLElement) {
          this.lastDrawerTrigger.focus()
        }
      }

      lockPageScroll(reason) {
        if (!reason) {
          return
        }

        this.pageScrollLockReasons ||= new Set()
        this.pageScrollLockReasons.add(reason)

        if (this.ownsPageScrollLock) {
          return
        }

        const documentRoot = document.documentElement
        const documentBody = document.body

        if (!documentRoot) {
          return
        }

        const currentCount =
          Number.parseInt(documentRoot.dataset.lpHeaderScrollLockCount || '0', 10) || 0

        if (currentCount === 0) {
          documentRoot.dataset.lpHeaderOriginalOverflow = documentRoot.style.overflow || ''

          if (documentBody) {
            documentBody.dataset.lpHeaderOriginalOverflow = documentBody.style.overflow || ''
          }
        }

        documentRoot.dataset.lpHeaderScrollLockCount = String(currentCount + 1)
        documentRoot.style.overflow = 'hidden'

        if (documentBody) {
          documentBody.style.overflow = 'hidden'
        }

        this.ownsPageScrollLock = true
      }

      unlockPageScroll(reason) {
        this.pageScrollLockReasons?.delete(reason)

        if (this.pageScrollLockReasons?.size) {
          return
        }

        this.releasePageScrollLock()
      }

      releasePageScrollLock() {
        if (!this.ownsPageScrollLock) {
          return
        }

        const documentRoot = document.documentElement
        const documentBody = document.body
        this.ownsPageScrollLock = false

        if (!documentRoot) {
          return
        }

        const currentCount =
          Number.parseInt(documentRoot.dataset.lpHeaderScrollLockCount || '0', 10) || 0
        const nextCount = Math.max(0, currentCount - 1)

        if (nextCount > 0) {
          documentRoot.dataset.lpHeaderScrollLockCount = String(nextCount)
          documentRoot.style.overflow = 'hidden'

          if (documentBody) {
            documentBody.style.overflow = 'hidden'
          }

          return
        }

        documentRoot.style.overflow = documentRoot.dataset.lpHeaderOriginalOverflow || ''
        delete documentRoot.dataset.lpHeaderOriginalOverflow
        delete documentRoot.dataset.lpHeaderScrollLockCount

        if (documentBody) {
          documentBody.style.overflow = documentBody.dataset.lpHeaderOriginalOverflow || ''
          delete documentBody.dataset.lpHeaderOriginalOverflow
        }
      }

      isActiveConnection(generation) {
        return (
          this.isConnected &&
          Boolean(this.connectionAbortController) &&
          this.connectionGeneration === generation
        )
      }
    },
  )
}
